import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { MaskLines } from "./Motion";

export function PageHero({ eyebrow, lines, intro, breadcrumb, image, bullets }) {
  return (
    <section className="relative tactical-grid border-b border-white/10 overflow-hidden" data-testid="page-hero">
      {image && (
        <div className="absolute inset-0">
          <img src={image} alt="" className="w-full h-full object-cover opacity-25" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/80 to-ink pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12 pb-12 sm:pb-16">
        {breadcrumb && (
          <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="breadcrumbs">
            {breadcrumb.map((b, i) => (
              <span key={b.path} className="flex items-center gap-2">
                {i > 0 && <span className="text-amber">//</span>}
                {i === breadcrumb.length - 1 ? (
                  <span className="text-amber">{b.label}</span>
                ) : (
                  <Link to={b.path} className="hover:text-amber transition-colors">{b.label}</Link>
                )}
              </span>
            ))}
          </nav>
        )}
        {eyebrow && (
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-amber mb-5">
            <MapPin className="w-3.5 h-3.5" /> {eyebrow}
          </p>
        )}
        <MaskLines
          lines={lines}
          className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl text-white max-w-4xl"
        />
        {intro && (
          <div className="mt-8 max-w-3xl space-y-5">
            {intro.map((p, i) => (
              <div key={i} className="contents">
                <p className={i === 0 ? "text-base sm:text-lg text-gray-300 leading-relaxed" : "text-sm sm:text-base text-gray-400 leading-relaxed"}>
                  {p}
                </p>
                {i === 0 && bullets && (
                  <ul className="space-y-2.5 my-4" data-testid="hero-bullets">
                    {bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm sm:text-base text-gray-300">
                        <span className="mt-2 w-2 h-2 bg-amber rotate-45 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function SectionBlock({ index, heading, paragraphs, bullets, children }) {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid={`content-section-${index}`}>
      <div className="flex items-baseline gap-4 mb-6">
        <span className="font-mono text-xs text-amber tracking-widest">{String(index + 1).padStart(2, "0")}</span>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white">{heading}</h2>
      </div>
      <div className="max-w-3xl space-y-4">
        {paragraphs?.map((p, i) => (
          <p key={i} className="text-sm sm:text-base text-gray-400 leading-relaxed">{p}</p>
        ))}
      </div>
      {bullets && (
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
              <span className="mt-1.5 w-2 h-2 bg-amber rotate-45 shrink-0" />
              {b}
            </li>
          ))}
        </ul>
      )}
      {children}
    </section>
  );
}

export function LinkPills({ items, prefix = "", labelFn, testIdPrefix, toFn }) {
  return (
    <div className="mt-6 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-links`}>
      {items.map((item) => (
        <Link
          key={item.slug}
          to={toFn ? toFn(item) : `${prefix}/${item.slug}`}
          data-testid={`${testIdPrefix}-link-${item.slug}`}
          className="font-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-white/10 bg-card text-gray-300 hover:border-amber hover:text-amber transition-colors duration-300"
        >
          {labelFn ? labelFn(item) : item.name}
        </Link>
      ))}
    </div>
  );
}

export function Loading() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loading">
      <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-amber">
        <span className="w-2 h-2 bg-amber animate-ping" /> Loading
      </div>
    </div>
  );
}
